import { Box, Drawer, Typography } from '@mui/material';
import { CompanyForm } from 'components/@admin/@companies/CompanyForm';
import CloseButton from 'components/@home/drawers/CloseButton';
import { Formik } from 'formik';
import { bool, func, object } from 'prop-types';
import React from 'react';
import { I18n } from 'utils/i18n';
import Company from 'services/api/Company';
import {
  useCreateCompanyMutation,
  useEditCompanyMutation,
  useListCompaniesQuery,
} from 'store/app/entities/CompaniesSlice';
import defaultReactionSet, { MAX_COMPANY_REACTIONS } from 'constants/reactions';
import privacyEs from './privacy_es.html';
import termsEs from './terms_es.html';

const getBody = async values => {
  const {
    address,
    allowAnonymousEmployees,
    billingContactName,
    billingContactSurname,
    businessName,
    city,
    codeType,
    contactEmail,
    country,
    defaultMobileNotifications,
    demo,
    divisions,
    dpoEmail,
    supportEmail,
    featureFlags,
    reactions,
    fromTemplate,
    language,
    languages,
    name,
    privacy,
    state,
    stock,
    stripeCustomerId,
    subDomain,
    subscription,
    template,
    terms,
    timeZone,
    vatIDNumber,
    webSiteUrl,
    zip,
    appName,
    appLogo,
    allowOnlyAuthorizedDevices,
    eSignatureProvider,
  } = values;
  const Mustache = (await import('mustache')).default;
  const allTags = new Set();
  Mustache.parse(privacy)
    .filter(([key, value]) => key === 'name' && value.startsWith('company.'))
    .forEach(([, curr]) => allTags.add(curr));
  Mustache.parse(terms)
    .filter(([key, value]) => key === 'name' && value.startsWith('company.'))
    .forEach(([, curr]) => allTags.add(curr));
  const c = Array.from(allTags).reduce((ret, key) => {
    const bodyKey = key.replace('company.', '');
    return { ...ret, [bodyKey]: values[bodyKey] || `{{${key}}}` };
  }, {});
  const renderedPrivacy = Mustache.render(privacy, { company: c });
  const renderedTerms = Mustache.render(terms, { company: c });
  const reactionsNormalized = [
    ...new Set((reactions || []).map(r => String(r).trim()).filter(Boolean)),
  ].slice(0, MAX_COMPANY_REACTIONS);
  const reactionsPayload = reactionsNormalized.length
    ? reactionsNormalized
    : [...defaultReactionSet];
  return {
    address,
    allowAnonymousEmployees: allowAnonymousEmployees?.[0] === 'on' ? 1 : 0,
    allowOnlyAuthorizedDevices: allowOnlyAuthorizedDevices?.[0] === 'on' ? 1 : 0,
    billingContactName,
    billingContactSurname,
    businessName,
    city,
    codeType,
    contactEmail,
    country,
    defaultMobileNotifications,
    demo: demo?.[0] === 'on' ? 1 : 0,
    divisions,
    dpoEmail,
    supportEmail,
    featureFlags,
    reactions: reactionsPayload,
    fromTemplate,
    language,
    languages,
    name,
    privacy: renderedPrivacy,
    state,
    stock,
    stripeCustomerId,
    subDomain,
    subscription: subscription?.status
      ? {
          ...subscription,
          status: subscription.status.toLowerCase(),
        }
      : subscription,
    template: template?.[0] === 'on' ? 1 : 0,
    terms: renderedTerms,
    timeZone,
    vatIDNumber,
    webSiteUrl,
    zip,
    appName,
    appLogo,
    eSignatureProvider,
  };
};

const CompanyDrawer = ({ company = {}, isOpen, onClose }) => {
  const isEdit = company?._id;
  const [createCompany, createCompanyStatus] = useCreateCompanyMutation();
  const [editCompany, editCompanyStatus] = useEditCompanyMutation();
  const { error, isLoading, isError } = {
    error: createCompanyStatus.error || editCompanyStatus.error,
    isLoading: createCompanyStatus.isLoading || editCompanyStatus.isLoading,
    isError: createCompanyStatus.isError || editCompanyStatus.isError,
  };
  const { data: { companies: templates } = {}, isFetching } = useListCompaniesQuery({
    template: true,
    perPage: 100,
    sortBy: 'name',
    sort: 'asc',
  });
  const validateESignatureProvider = eSignatureProvider => {
    if (eSignatureProvider == null) {
      return {};
    }

    const errors = {};

    if (
      (eSignatureProvider.isActive === false || eSignatureProvider.isActive === 0) &&
      eSignatureProvider.name
    ) {
      return errors;
    }

    // Platform SignRequest + Ommnio key is only `null`; any non-null provider needs credentials except OmmnioSign
    if (eSignatureProvider.name === 'ommniosign') {
      return errors;
    }
    if (!eSignatureProvider.config?.apiKey) {
      errors.eSignatureProvider = I18n.t('CompanyForm.ESignatureProvider.ValidationError');
    }

    return errors;
  };

  const handleSubmit = async ({
    _id,
    logo,
    logoDark,
    onBoardingImage,
    eSignatureProvider,
    ...values
  }) => {
    const body = await getBody({ ...values, eSignatureProvider });
    const ret = _id ? await editCompany({ _id, body }) : await createCompany(body);
    const companyId = ret.data.data.company._id;

    const images = { logo, logoDark, onBoardingImage };
    await Promise.all(
      Object.entries(images).map(async ([key, value]) => {
        if (value?.length === 1 && value[0] instanceof File) {
          images[key] = await Company.upload(companyId, value[0]);
        } else {
          delete images[key];
        }
      }),
    );

    if (Object.keys(images).length) {
      await editCompany({ _id: companyId, body: images });
    }

    if (!ret.error) {
      onClose();
    }
  };
  return (
    <Drawer disableEnforceFocus anchor="right" open={isOpen} onClose={onClose}>
      <Box
        sx={{
          display: 'flex',
          flex: 1,
          justifyContent: 'center',
          maxWidth: 'calc(100vw - 40px)',
          width: {
            xs: '100%',
            md: 1100,
          },
          height: '100vh',
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            paddingTop: 2.5,
            paddingLeft: 2.5,
          }}
        >
          <CloseButton onClick={onClose} />
        </Box>
        <Box
          sx={{
            display: 'flex',
            flex: 1,
            minWidth: 500,
            flexDirection: 'column',
            padding: 2.5,
          }}
        >
          <Typography variant="h6" component="h1">
            {isEdit
              ? I18n.t(`CompanyDrawer.Edit`, { name: company.name })
              : I18n.t(`CompanyDrawer.Create company`)}
          </Typography>
          <Formik
            initialValues={{
              ...company,
              timeZone: company.timeZone || 'Europe/Madrid',
              language: company.language || 'es',
              languages: company.languages || ['es', 'en'],
              allowAnonymousEmployees: company.allowAnonymousEmployees ? ['on'] : [],
              allowOnlyAuthorizedDevices: company.allowOnlyAuthorizedDevices ? ['on'] : [],
              demo: company.demo ? ['on'] : [],
              template: company.template ? ['on'] : [],
              divisions: company.divisions || [],
              featureFlags: company.featureFlags || [],
              reactions: company.reactions?.length ? company.reactions : [...defaultReactionSet],
              privacy: company.privacy || privacyEs,
              terms: company.terms || termsEs,
              defaultMobileNotifications: company.defaultMobileNotifications || 'Restricted',
              eSignatureProvider: (() => {
                const esp = company.eSignatureProvider;
                if (!esp || Object.keys(esp).length === 0) return null;
                // Mongoose / partial hydration can yield `{ isActive: false }` with no `name` — not "disabled"
                if ((esp.isActive === false || esp.isActive === 0) && !esp.name) return null;
                if (esp.isActive === false || esp.isActive === 0) {
                  return {
                    name: esp.name === 'signrequest' ? 'ommniosign' : esp.name || 'ommniosign',
                    config: esp.config || {},
                    isActive: false,
                  };
                }
                const name = esp.name || 'signrequest';
                if (name === 'ommniosign') {
                  return { name: 'ommniosign', config: esp.config || {}, isActive: true };
                }
                // Platform SignRequest + Ommnio API key is represented as `null`
                if (name === 'signrequest' && !esp.config?.apiKey) return null;
                return {
                  name: esp.name || 'signrequest',
                  config: esp.config || {},
                  isActive: true,
                };
              })(),
            }}
            validate={values => validateESignatureProvider(values.eSignatureProvider)}
            onSubmit={handleSubmit}
          >
            <CompanyForm
              company={company}
              onClose={onClose}
              error={error}
              isLoading={isLoading || isFetching}
              isError={isError}
              templates={templates || []}
            />
          </Formik>
        </Box>
      </Box>
    </Drawer>
  );
};

CompanyDrawer.propTypes = {
  company: object,
  isOpen: bool.isRequired,
  onClose: func.isRequired,
};

export default CompanyDrawer;
